import { useState, useEffect, useCallback } from "react";
import { wordsData } from "../data/words";
import { useSpeech } from "../utils/speech";
import { GameModes } from "../data/gameModes";

export const useGame = () => {
  const { speakWord } = useSpeech();
  const [currentGameMode, setCurrentGameMode] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentScore, setCurrentScore] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(false);
  const [gameFinished, setGameFinished] = useState(false);
  const [gameQuestions, setGameQuestions] = useState([]);
  const [gameResults, setGameResults] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(60);
  const [timerActive, setTimerActive] = useState(false);

  const showResults = () => {
    setTimerActive(false);
    setGameFinished(true);
  };

  const nextQuestion = () => {
    setCurrentQuestionIndex((prevIndex) => {
      if (prevIndex < gameQuestions.length - 1) {
        return prevIndex + 1;
      } else {
        showResults();
        return prevIndex; // Keep the index the same if showing results
      }
    });
  };

  const startGame = (modeName, category = "all") => {
    const mode = GameModes[modeName];
    if (!mode) {
      console.error("Invalid game mode:", modeName);
      return;
    }

    setCurrentGameMode(modeName);
    setCurrentQuestionIndex(0);
    setCurrentScore(0);
    setQuestionsAnswered(false);
    setGameResults([]);
    setTimeRemaining(mode.timeLimit || 60); // Use mode's timeLimit or default
    setTimerActive(modeName === "TIME_CHALLENGE");
    setGameFinished(false);

    let filteredWords = wordsData;
    if (category !== "all") {
      filteredWords = wordsData.filter((word) => word.category === category);
    }

    const shuffled = [...filteredWords].sort(() => Math.random() - 0.5);

    console.log(category);
    if (mode.wordCount) {
      setGameQuestions(shuffled.slice(0, mode.wordCount));
    } else {
      setGameQuestions(shuffled); // For modes like time challenge where word count is not fixed
    }
  };

  useEffect(() => {
    if (timerActive && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (timerActive && timeRemaining === 0) {
      showResults();
    }
  }, [timerActive, timeRemaining]);

  const getOptions = useCallback((correctOption, field) => {
    let options = [correctOption];
    const wrongOptions = [...wordsData]
      .filter((w) => w[field] !== correctOption[field])
      .sort(() => Math.random() - 0.5)
      .slice(0, 2);
    return [...options, ...wrongOptions].sort(() => Math.random() - 0.5);
  }, []);

  const generateQuestionData = useCallback(
    (questionIndex, questions, mode) => {
      if (questions.length === 0) return null;

      const question = questions[questionIndex];

      let questionGameModeId;
      if (mode === "TIME_CHALLENGE") {
        const availableModeIds = [
          GameModes.ENGLISH_TO_CHINESE.id,
          GameModes.CHINESE_TO_ENGLISH.id,
          GameModes.PICTURE_TO_WORD.id,
          GameModes.LISTEN_ENGLISH_WORD.id,
          GameModes.LISTEN_CHINESE_WORD.id,
        ];
        questionGameModeId =
          availableModeIds[Math.floor(Math.random() * availableModeIds.length)];
      } else {
        questionGameModeId = GameModes[mode].id;
      }

      let options = [];
      let correctIndex = 0;
      let questionText = "";
      let questionEmoji = "";
      let replayAudioWord = "";

      if (questionGameModeId === GameModes.ENGLISH_TO_CHINESE.id) {
        // 英→中
        questionText = question.english;
        options = getOptions(question, "chinese");
        correctIndex = options.findIndex((o) => o.chinese === question.chinese);
      } else if (questionGameModeId === GameModes.CHINESE_TO_ENGLISH.id) {
        // 中→英
        questionText = question.chinese;
        options = getOptions(question, "english");
        correctIndex = options.findIndex((o) => o.english === question.english);
      } else if (questionGameModeId === GameModes.PICTURE_TO_WORD.id) {
        // 圖→英
        questionEmoji = question.emoji;
        questionText = "這是什麼？";
        options = getOptions(question, "english");
        correctIndex = options.findIndex((o) => o.english === question.english);
      } else if (questionGameModeId === GameModes.LISTEN_ENGLISH_WORD.id) {
        // 聲→英
        replayAudioWord = question.english;
        options = getOptions(question, "english");
        correctIndex = options.findIndex((o) => o.english === question.english);
      } else if (questionGameModeId === GameModes.LISTEN_CHINESE_WORD.id) {
        // 聲→中
        replayAudioWord = question.english;
        options = getOptions(question, "chinese");
        correctIndex = options.findIndex((o) => o.chinese === question.chinese);
      }

      return {
        question,
        questionText,
        questionEmoji,
        replayAudioWord,
        options,
        correctIndex,
        questionGameMode: questionGameModeId,
      };
    },
    [getOptions]
  );

  const [currentQuestionData, setCurrentQuestionData] = useState(null);

  useEffect(() => {
    if (gameQuestions.length > 0 && currentGameMode) {
      const data = generateQuestionData(
        currentQuestionIndex,
        gameQuestions,
        currentGameMode
      );
      setCurrentQuestionData(data);
      setQuestionsAnswered(false); // Reset when a new question is loaded
    }
  }, [
    currentQuestionIndex,
    gameQuestions,
    currentGameMode,
    generateQuestionData,
  ]);

  const selectAnswer = (
    selectedOption,
    correctOption,
    options,
    questionGameModeId // This is the ID from loadQuestion
  ) => {
    if (questionsAnswered) return;
    setQuestionsAnswered(true);

    let isCorrect = false;
    if (
      questionGameModeId === GameModes.ENGLISH_TO_CHINESE.id ||
      questionGameModeId === GameModes.LISTEN_CHINESE_WORD.id
    ) {
      isCorrect = selectedOption.chinese === correctOption.chinese;
    } else {
      isCorrect = selectedOption.english === correctOption.english;
    }

    setGameResults((prev) => [
      ...prev,
      {
        question: gameQuestions[currentQuestionIndex],
        selectedAnswer: selectedOption,
        isCorrect: isCorrect,
        gameMode: questionGameModeId, // Use the ID here
      },
    ]);

    if (isCorrect) {
      setCurrentScore((prev) => prev + 1);
    }

    if (currentGameMode === "TIME_CHALLENGE") {
      nextQuestion();
    } else {
      // For non-time challenge modes, check if it's the last question
      if (currentQuestionIndex === gameQuestions.length - 1) {
        showResults();
      } else {
        nextQuestion();
      }
    }
  };

  const backToMenu = () => {
    setCurrentGameMode(null);
    setCurrentQuestionIndex(0);
    setCurrentScore(0);
    setQuestionsAnswered(false);
    setGameQuestions([]);
    setGameResults([]);
    setTimeRemaining(60);
    setTimerActive(false);
  };

  return {
    currentGameMode,
    currentQuestionIndex,
    currentScore,
    questionsAnswered,
    gameQuestions,
    gameResults,
    timeRemaining,
    timerActive,
    gameFinished,
    startGame,
    selectAnswer,
    nextQuestion,
    showResults,
    currentQuestionData,
    backToMenu,
    speakWord,
  };
};
